import React, { useEffect, useMemo, useReducer } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { DataTable } from '@packages/ui/patterns/DataTable';
import { Typography, Spinner } from '@packages/ui/primitives';
import { resolveFieldDisplayValue, resolveSyncFormatter } from '../utils/resolveFieldDisplayValue';
import { ActionRegistry } from '@packages/table-builder/registry';
import type { BatchTableColumn, FormBatchTableNode } from '../types/layout';
import type { FormFieldMapping } from '../types/fieldMapping';
import { FormFieldAdapter } from './fields/FormFieldAdapter';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';
import { evaluateCompute } from '../evaluators/computeEvaluator';

interface BatchTableLoadState {
  loading: boolean;
  hydrated: boolean;
}

type BatchTableLoadAction =
  | { type: 'START' }
  | { type: 'DONE' };

function batchTableLoadReducer(state: BatchTableLoadState, action: BatchTableLoadAction): BatchTableLoadState {
  switch (action.type) {
    case 'START':
      return { loading: true, hydrated: state.hydrated };
    case 'DONE':
      return { loading: false, hydrated: true };
  }
}

function columnToFieldMapping(column: BatchTableColumn): FormFieldMapping {
  return {
    name: column.key,
    def: {
      label: column.label,
      schema: z.unknown().optional(),
      adapterHint: column.adapterHint ?? 'text',
    },
  };
}

function resolveArrayFieldName(name: string, namePrefix?: string): string {
  return namePrefix ? `${namePrefix}.${name}` : name;
}

interface FormBatchTableRendererProps {
  node: FormBatchTableNode;
  namePrefix?: string;
}

export const FormBatchTableRenderer = React.memo(function FormBatchTableRenderer({
  node,
  namePrefix,
}: FormBatchTableRendererProps): React.ReactElement {
  const { form } = useFormEngineForm();
  const { meta, isReadOnly, getOptionProp } = useFormEngineState();
  const arrayName = resolveArrayFieldName(node.name, namePrefix);
  const tableReadOnly = isReadOnly || node.readOnly === true;

  const { fields } = useFieldArray({
    control: form.control as any,
    name: arrayName,
  });

  const [loadState, dispatchLoad] = useReducer(batchTableLoadReducer, {
    loading: Boolean(node.dataSource?.actionId),
    hydrated: false,
  });

  useEffect(() => {
    const metaKey = node.dataSource?.metaKey;
    if (metaKey) {
      const rows = meta[metaKey];
      if (Array.isArray(rows) && rows.length > 0) {
        const current = form.getValues(arrayName as never);
        if (!Array.isArray(current) || current.length === 0) {
          form.setValue(arrayName as never, rows as never, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
        dispatchLoad({ type: 'DONE' });
      }
      return;
    }

    if (loadState.hydrated) return;

    const actionId = node.dataSource?.actionId;
    if (!actionId) return;

    let cancelled = false;
    dispatchLoad({ type: 'START' });

    void ActionRegistry.executeData(actionId, {})
      .then((result) => {
        if (cancelled) return;
        const raw = result as Record<string, unknown>;
        const rows = (raw.rows as unknown[]) ?? (Array.isArray(result) ? result : []);
        if (Array.isArray(rows)) {
          form.setValue(arrayName as never, rows as never, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
        dispatchLoad({ type: 'DONE' });
      })
      .catch(() => {
        if (!cancelled) dispatchLoad({ type: 'DONE' });
      });

    return () => {
      cancelled = true;
    };
  }, [
    arrayName,
    form,
    loadState.hydrated,
    meta,
    node.dataSource?.actionId,
    node.dataSource?.metaKey,
  ]);

  useWatch({
    control: form.control,
    name: arrayName as never,
  });

  const footerValues = useMemo(() => {
    if (!node.footerSummary?.length) return {};

    const values = form.getValues() as Record<string, unknown>;
    const rows = (getNestedValue(values, arrayName) as unknown[]) ?? [];

    return Object.fromEntries(
      node.footerSummary.map((footer) => {
        const computed = footer.compute
          ? evaluateCompute(footer.compute, {
              values,
              meta,
              fieldValue: rows,
              getOptionProp,
            })
          : rows.reduce<number>((sum, row) => {
              if (!row || typeof row !== 'object') return sum;
              const val = (row as Record<string, unknown>)[footer.key];
              return sum + (Number(val) || 0);
            }, 0);

        return [footer.key, computed];
      }),
    );
  }, [arrayName, form, getOptionProp, meta, node.footerSummary, fields.length]);

  if (loadState.loading) {
    return <Spinner size='sm' />;
  }

  return (
    <DataTable.Root semanticId={`${arrayName}BatchTable`}>
      <DataTable.Content>
        <DataTable.Header>
          <DataTable.Row>
            {node.columns.map((column) => (
              <DataTable.HeaderCell
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.label}
              </DataTable.HeaderCell>
            ))}
          </DataTable.Row>
        </DataTable.Header>
        <DataTable.Body>
          {fields.map((row, rowIndex) => (
            <DataTable.Row key={row.id} data-batch-table-row={rowIndex}>
              {node.columns.map((column) => {
                const cellReadOnly = tableReadOnly || column.readOnly === true;
                const rowPrefix = `${arrayName}.${rowIndex}`;
                const field = columnToFieldMapping(column);

                return (
                  <DataTable.Cell key={column.key}>
                    {cellReadOnly ? (
                      <BatchTableReadOnlyCell
                        arrayName={arrayName}
                        rowIndex={rowIndex}
                        column={column}
                      />
                    ) : (
                      <FormFieldAdapter field={field} namePrefix={rowPrefix} />
                    )}
                  </DataTable.Cell>
                );
              })}
            </DataTable.Row>
          ))}
        </DataTable.Body>
      </DataTable.Content>

      {node.footerSummary?.length ? (
        <DataTable.Footer>
          {node.footerSummary.map((footer) => (
            <Typography key={footer.key} variant='body-s'>
              {footer.label}: {String(footerValues[footer.key] ?? '-')}
            </Typography>
          ))}
        </DataTable.Footer>
      ) : null}
    </DataTable.Root>
  );
});

interface BatchTableReadOnlyCellProps {
  arrayName: string;
  rowIndex: number;
  column: BatchTableColumn;
}

function BatchTableReadOnlyCell({
  arrayName,
  rowIndex,
  column,
}: BatchTableReadOnlyCellProps): React.ReactElement {
  const { form } = useFormEngineForm();
  const fieldPath = `${arrayName}.${rowIndex}.${column.key}`;
  const value = useWatch({ control: form.control, name: fieldPath as never });
  const field = columnToFieldMapping(column);

  return (
    <Typography variant='body-s'>
      {resolveFieldDisplayValue(value, field, {
        formatter: resolveSyncFormatter(field.def.formatter as FormFieldMapping['def']['formatter']),
        addon: field.adapterProps?.endAddon ?? field.adapterProps?.startAddon,
      })}
    </Typography>
  );
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}
